import React, { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011';

interface PromptButtonProps {
  socket: Socket;
  roomId: string;
  accessToken: string;
}

export const PromptButton: React.FC<PromptButtonProps> = ({ socket, roomId, accessToken }) => {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPrompt = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/prompts/random`,
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      const newPrompt = response.data.prompt.question;

      setPrompt(newPrompt);

      // Share with partner
      socket.emit('share-prompt', { roomId, prompt: newPrompt });
    } catch (err) {
      console.error('Failed to fetch prompt:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Listen for partner sharing prompt
    socket.on('prompt-received', ({ prompt: receivedPrompt }) => {
      setPrompt(receivedPrompt);
    });

    return () => {
      socket.off('prompt-received');
    };
  }, [socket]);

  return (
    <div style={styles.container}>
      {prompt ? (
        <div style={styles.promptDisplay}>
          <div style={styles.promptIcon}>💬</div>
          <p style={styles.promptText}>{prompt}</p>
          <button onClick={fetchPrompt} disabled={loading} style={styles.newButton}>
            {loading ? 'Loading...' : 'New Prompt'}
          </button>
        </div>
      ) : (
        <button onClick={fetchPrompt} disabled={loading} style={styles.button}>
          {loading ? 'Loading...' : '💬 Need a conversation starter?'}
        </button>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'absolute',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
  },
  button: {
    padding: '0.75rem 1.5rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    transition: 'all 0.3s ease',
  },
  promptDisplay: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    padding: '1.5rem',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    maxWidth: '400px',
    textAlign: 'center',
    border: '2px solid rgba(102, 126, 234, 0.3)',
  },
  promptIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  },
  promptText: {
    fontSize: '1.1rem',
    color: '#333',
    marginBottom: '1rem',
    lineHeight: 1.5,
    fontWeight: '500',
  },
  newButton: {
    padding: '0.5rem 1.5rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
};
